import { Viewer } from "cesium";
import { deferred, Guard } from "lib/actions/util";
import { AttachablePluginInterface } from "./CesiumWrapper";
import TilesetStylingPlugin, {
  TilesetStylePriority,
} from "./TilesetStylingPlugin";
import {
  ObservableProperty,
  DerivedObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import TilesetPlugin, { TilesetFeature } from "./TilesetPlugin";
import FeatureRayPickerPlugin from "./FeatureRayPickerPlugin";

export type HideMode = "full" | "partial";

type HiddenElement = { id: string; tilesetId: string; name: string };

/**
 * One step of hide history: what was hidden, and which of those were hidden
 * completely, so undo and redo bring each element back the way it was hidden.
 */
type HistoryEntry = {
  hiddenElements: HiddenElement[];
  invisibleElementKeys: ReadonlySet<string>;
};

export type VisibilityToolSnapshot = {
  version: 1;
  hiddenElements: HiddenElement[];
};

export function isVisibilityStateApplied(
  state: VisibilityToolSnapshot | undefined,
): boolean {
  return (state?.hiddenElements?.length ?? 0) > 0;
}

function getElementKey(elementId: string, tilesetId: string) {
  return `${tilesetId}:${elementId}`;
}

const NAME_PROPERTIES = [
  "Id",
  "GUID",
  "Name",
  "Type",
  "Level",
  "Units",
  "Family",
  "Category",
  "ApplicationId",
  "IFC Type",
];

export default class TilesetElementVisibilityPlugin
  implements
    AttachablePluginInterface,
    ActivatableToolInterface,
    SerializableToolInterface<VisibilityToolSnapshot>,
    BackgroundableToolInterface
{
  private _viewer = deferred<Viewer>();
  private _isActive = false;
  private _hideMode: HideMode = "partial";
  private _highlighted: { tilesetId: string; elementId: string } = null;
  private _detachClickHandler: (() => void) | null = null;
  private _detachMoveFeatureHandler: (() => void) | null = null;
  private _detachMoveEmptyHandler: (() => void) | null = null;
  private _detachClickEmptyHandler: (() => void) | null = null;

  private readonly _hiddenElements = new ObservableProperty<HiddenElement[]>(
    [],
  );
  readonly isApplied = new DerivedObservableProperty(
    () => isVisibilityStateApplied(this.capture()),
    [this._hiddenElements],
  );

  private readonly _history: HistoryEntry[] = [
    { hiddenElements: [], invisibleElementKeys: new Set() },
  ];
  private _historyIndex = 0;
  private readonly _canUndo = new ObservableProperty<boolean>(false);
  private readonly _canRedo = new ObservableProperty<boolean>(false);
  private readonly _isHidingCompletely = new ObservableProperty<boolean>(true);
  /**
   * Elements hidden while "hide completely" was on, which stay gone while the
   * tool is open rather than showing see-through. Only for while the tool is
   * open: Views save just the hidden elements, as they always have.
   */
  private readonly _invisibleElementKeys = new Set<string>();

  constructor(
    private readonly _tilesetStylingPlugin: TilesetStylingPlugin,
    private readonly _featureRayPicker: FeatureRayPickerPlugin,
  ) {}

  get hiddenElements(): ReadonlyObservableProperty<HiddenElement[]> {
    return this._hiddenElements;
  }

  get canUndo(): ReadonlyObservableProperty<boolean> {
    return this._canUndo;
  }

  get canRedo(): ReadonlyObservableProperty<boolean> {
    return this._canRedo;
  }

  /**
   * Whether elements hidden from now on disappear completely while the tool is
   * open, so the elements behind them can be clicked. Otherwise they stay as
   * see-through ghosts that can be clicked to show them again. Switching it
   * doesn't change elements that are already hidden.
   */
  get isHidingCompletely(): ReadonlyObservableProperty<boolean> {
    return this._isHidingCompletely;
  }

  setIsHidingCompletely(isHidingCompletely: boolean) {
    this._isHidingCompletely.set(isHidingCompletely);
  }

  setViewer(viewer: Viewer): void {
    this._viewer.ready(viewer);
  }

  detachViewer(): void {
    this._viewer.dispose();
    this._viewer = deferred();
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    this._isActive = true;
    this.setHideMode("partial");

    this._detachClickHandler =
      this._featureRayPicker.leftClickFeatureEvent.addHandler((object) => {
        if (!TilesetPlugin.isFeature(object)) return;
        const elementId = TilesetPlugin.getElementIdFromFeature(object);
        const tilesetId = TilesetPlugin.getTilesetIdFromTileset(object.tileset);
        const name = this.extractFeatureName(object);
        const current = this._hiddenElements.getSnapshot();

        if (
          current.find((e) => e.id === elementId && e.tilesetId === tilesetId)
        ) {
          this.unhideElement(elementId, tilesetId);
        } else {
          this.hideElement(elementId, tilesetId, name);
        }
      });

    this._detachMoveFeatureHandler =
      this._featureRayPicker.mouseMoveFeatureEvent.addHandler((feature) => {
        void this.highlightFeature(feature);
      });

    this._detachMoveEmptyHandler =
      this._featureRayPicker.mouseMoveEmptyEvent.addHandler(() => {
        void this.unhighlightElement();
      });

    this._detachClickEmptyHandler =
      this._featureRayPicker.leftClickEmptyEvent.addHandler(() => {
        void this.unhighlightElement();
      });
  }

  deactivate(): void {
    this._isActive = false;
    this._detachClickHandler?.();
    this._detachMoveFeatureHandler?.();
    this._detachMoveEmptyHandler?.();
    this._detachClickEmptyHandler?.();
    this._detachClickHandler = null;
    this._detachMoveFeatureHandler = null;
    this._detachMoveEmptyHandler = null;
    this._detachClickEmptyHandler = null;
    void this.unhighlightElement();
    this.setHideMode("full");
    // The next time the tool opens, hiding starts out complete again.
    this._isHidingCompletely.set(true);
  }

  setHideMode(mode: HideMode) {
    this._hideMode = mode;
    this.applyHiddenRules();
  }

  hideElement(elementId: string, tilesetId: string, name = "") {
    Guard.require(elementId, "elementId");
    Guard.require(tilesetId, "tilesetId");
    this.removeHiddenRules();
    const current = this._hiddenElements.getSnapshot();
    const next = [...current, { id: elementId, tilesetId, name }];
    const elementKey = getElementKey(elementId, tilesetId);
    if (this._isHidingCompletely.getSnapshot()) {
      this._invisibleElementKeys.add(elementKey);
    } else {
      this._invisibleElementKeys.delete(elementKey);
    }
    this._hiddenElements.set(next);
    this.applyHiddenRules();
    this.pushHistory(next);
  }

  hideFeature(feature: TilesetFeature) {
    Guard.require(feature, "feature");
    const tilesetId = TilesetPlugin.getTilesetIdFromTileset(feature.tileset);
    const elementId = TilesetPlugin.getElementIdFromFeature(feature);
    this.hideElement(elementId, tilesetId);
  }

  unhideElement(elementId: string, tilesetId: string) {
    Guard.require(elementId, "elementId");
    Guard.require(tilesetId, "tilesetId");
    this.removeHiddenRules();
    const current = this._hiddenElements.getSnapshot();
    const next = current.filter(
      (x) => x.tilesetId !== tilesetId || x.id !== elementId,
    );
    this._invisibleElementKeys.delete(getElementKey(elementId, tilesetId));
    this._hiddenElements.set(next);
    this.applyHiddenRules();
    this.pushHistory(next);
  }

  unhideFeature(feature: TilesetFeature) {
    Guard.require(feature, "feature");
    const tilesetId = TilesetPlugin.getTilesetIdFromTileset(feature.tileset);
    const elementId = TilesetPlugin.getElementIdFromFeature(feature);
    this.unhideElement(elementId, tilesetId);
  }

  capture(): VisibilityToolSnapshot {
    return { version: 1, hiddenElements: this._hiddenElements.getSnapshot() };
  }

  restore(state: VisibilityToolSnapshot | undefined): void {
    this.removeHiddenRules();
    this._invisibleElementKeys.clear();
    const next = state?.hiddenElements ?? [];
    this._hiddenElements.set(next);
    this.applyHiddenRules();
    this.resetHistory(next);
  }

  isStateApplied(state: VisibilityToolSnapshot | undefined): boolean {
    return isVisibilityStateApplied(state);
  }

  isMatchingState(state: VisibilityToolSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  clear(): void {
    this.removeHiddenRules();
    this._invisibleElementKeys.clear();
    this._hiddenElements.set([]);
    this.applyHiddenRules();
    this.resetHistory([]);
  }

  undo(): void {
    if (!this._canUndo.getSnapshot()) return;
    this._historyIndex--;
    this.applyHistoryState();
  }

  redo(): void {
    if (!this._canRedo.getSnapshot()) return;
    this._historyIndex++;
    this.applyHistoryState();
  }

  private applyHistoryState(): void {
    const entry = this._history[this._historyIndex];
    this.removeHiddenRules();
    this._invisibleElementKeys.clear();
    for (const key of entry.invisibleElementKeys) {
      this._invisibleElementKeys.add(key);
    }
    this._hiddenElements.set(entry.hiddenElements);
    this.applyHiddenRules();
    this.updateHistoryAvailability();
  }

  private pushHistory(state: HiddenElement[]): void {
    if (this._historyIndex < this._history.length - 1) {
      this._history.splice(this._historyIndex + 1);
    }
    this._history.push(this.createHistoryEntry(state));
    this._historyIndex++;
    this.updateHistoryAvailability();
  }

  private resetHistory(state: HiddenElement[]): void {
    this._history.length = 0;
    this._history.push(this.createHistoryEntry(state));
    this._historyIndex = 0;
    this.updateHistoryAvailability();
  }

  private createHistoryEntry(state: HiddenElement[]): HistoryEntry {
    return {
      hiddenElements: state,
      invisibleElementKeys: new Set(this._invisibleElementKeys),
    };
  }

  private updateHistoryAvailability(): void {
    this._canUndo.set(this._historyIndex > 0);
    this._canRedo.set(this._historyIndex < this._history.length - 1);
  }

  async highlightElement(elementId: string, tilesetId: string) {
    Guard.require(elementId, "elementId");
    Guard.require(tilesetId, "tilesetId");
    await this.unhighlightElement();
    this._highlighted = { elementId, tilesetId };
    this._tilesetStylingPlugin.setRule({
      priority: TilesetStylePriority.ElementVisibilityHighlighted,
      elementStyle: {
        tilesetId: tilesetId,
        elementId: elementId,
        color: "red",
        opacity: 0.15,
      },
    });
    await this._tilesetStylingPlugin.applyRules();
  }

  async highlightFeature(feature: TilesetFeature) {
    Guard.require(feature, "feature");
    const tilesetId = TilesetPlugin.getTilesetIdFromTileset(feature.tileset);
    const elementId = TilesetPlugin.getElementIdFromFeature(feature);
    this.highlightElement(elementId, tilesetId);
  }

  async unhighlightElement() {
    if (!this._highlighted) return;
    this._tilesetStylingPlugin.removeRule({
      priority: TilesetStylePriority.ElementVisibilityHighlighted,
      elementStyle: {
        tilesetId: this._highlighted.tilesetId,
        elementId: this._highlighted.elementId,
      },
    });
    await this._tilesetStylingPlugin.applyRules();
    this._highlighted = null;
  }

  private extractFeatureName(feature: TilesetFeature): string {
    for (const prop of NAME_PROPERTIES) {
      const value = feature.getProperty(prop);
      if (value) return value;
    }
    return "";
  }

  private removeHiddenRules() {
    for (const x of this._hiddenElements.getSnapshot()) {
      this._tilesetStylingPlugin.removeRule({
        priority: TilesetStylePriority.ElementVisibilityHidden,
        elementStyle: {
          tilesetId: x.tilesetId,
          elementId: x.id,
        },
      });
    }
  }

  private applyHiddenRulesForElement(elementId: string, tilesetId: string) {
    this._tilesetStylingPlugin.setRule({
      priority: TilesetStylePriority.ElementVisibilityHidden,
      elementStyle: {
        tilesetId,
        elementId,
        hidden:
          this._hideMode === "full" ||
          this._invisibleElementKeys.has(getElementKey(elementId, tilesetId)),
        color: "white",
        opacity: 0.4,
      },
    });
  }

  private async applyHiddenRules() {
    for (const x of this._hiddenElements.getSnapshot()) {
      this.applyHiddenRulesForElement(x.id, x.tilesetId);
    }
    await this._tilesetStylingPlugin.applyRules();
  }
}
